import { useMutation } from '@apollo/client';
import { Actions, Divider, Dropdown, Form, Header, Text } from '@gasbuddy/react-components';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { noop } from '../../../lib/utils';
import StationPropType from '../../prop-types/station';
import StationInfoBox from '../StationInfoBox';

export const UPDATE_STATION_STATUS = gql`
  mutation UpdateStationStatus($id: ID!, $hasDiesel: Boolean, $hasGas: Boolean, $hasPower: Boolean) {
    updateStationStatus(id: $id, hasDiesel: $hasDiesel, hasFuel: $hasGas, hasPower: $hasPower) {
      success
    }
  }
`;

function generateOptions(t, label) {
  return [
    { label: t('status.unknown', { ns: 'miscellaneous' }), value: -1 },
    { label: t(`outage.has${label}`, { ns: 'station' }), value: 1 },
    { label: t(`outage.doesNotHave${label}`, { ns: 'station' }), value: 0 },
  ];
}


export default function OutageForm({
  includeDiesel,
  onCancel,
  onComplete,
  station,
}) {
  const { id: stationId } = station;
  const { t } = useTranslation(['station', 'miscellaneous']);
  const [statuses, setStatuses] = useState({
    hasDiesel: -1,
    hasGas: -1,
    hasPower: -1,
  });

  const [updateStationStatus, { loading: isUpdating }] = useMutation(UPDATE_STATION_STATUS, {
    onCompleted: onComplete,
  });

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    updateStationStatus({
      variables: {
        id: stationId,
        hasDiesel: statuses.hasDiesel !== -1 ? !!parseInt(statuses.hasDiesel, 10) : undefined,
        hasGas: statuses.hasGas !== -1 ? !!parseInt(statuses.hasGas, 10) : undefined,
        hasPower: statuses.hasPower !== -1 ? !!parseInt(statuses.hasPower, 10) : undefined,
      },
    });
  }, [updateStationStatus, stationId, statuses]);

  const handleChange = useCallback(({ target: { name, value } }) => {
    if (statuses[name] !== value) {
      setStatuses(oldStatuses => ({
        ...oldStatuses,
        [name]: value,
      }));
    }
  }, [statuses]);

  const canSubmit = (includeDiesel && statuses.hasDiesel !== -1) || statuses.hasGas !== -1 || statuses.hasPower !== -1;

  const gasOptions = useMemo(() => generateOptions(t, 'Gas'), [t]);
  const powerOptions = useMemo(() => generateOptions(t, 'Power'), [t]);
  const dieselOptions = useMemo(() => generateOptions(t, 'Diesel'), [t]);

  return (
    <Fragment>
      <Header as="h3">
        {t('outage.formHeader', { ns: 'station' })}
      </Header>
      <StationInfoBox station={station} />
      <Divider />
      <Form onSubmit={handleSubmit} loading={isUpdating}>
        <Text as="label" htmlFor="hasPower" bold>
          {t('outage.formPowerLabel', { ns: 'station' })}
        </Text>
        <Dropdown
          id="hasPower"
          name="hasPower"
          options={powerOptions}
          onChange={handleChange}
        />
        <Text as="label" htmlFor="hasGas" bold>
          {t('outage.formGasLabel', { ns: 'station' })}
        </Text>
        <Dropdown
          id="hasGas"
          name="hasGas"
          options={gasOptions}
          onChange={handleChange}
        />
        {includeDiesel && (
          <Fragment>
            <Text as="label" htmlFor="hasDiesel" bold>
              {t('outage.formDieselLabel', { ns: 'station' })}
            </Text>
            <Dropdown
              id="hasDiesel"
              name="hasDiesel"
              options={dieselOptions}
              onChange={handleChange}
            />
          </Fragment>
        )}
        <Divider />
        <Actions>
          <Actions.Button
            primary
            fluid
            type="submit"
            desktop={6}
            disabled={!canSubmit}
          >
            {t('outage.submitStationStatus', { ns: 'station' })}
          </Actions.Button>
          <Actions.Button
            secondary
            fluid
            onClick={onCancel}
            desktop={4}
          >
            {t('actions.cancel', { ns: 'miscellaneous' })}
          </Actions.Button>
        </Actions>
      </Form>
    </Fragment>
  );
}

OutageForm.propTypes = {
  includeDiesel: PropTypes.bool,
  onCancel: PropTypes.func,
  onComplete: PropTypes.func,
  station: StationPropType,
};

OutageForm.defaultProps = {
  includeDiesel: false,
  onCancel: noop,
  onComplete: noop,
  station: {},
};
